// ══════════════════════════════════════════════════════════════════════════════════════════════
//  EL MOTOR DE LA TRADUCCIÓN: PURO, SIN IMPORTS (B-2259, t523, 27-oct-2026)
// ══════════════════════════════════════════════════════════════════════════════════════════════
//
//  Decide qué texto sale para una clave. No sabe de React, ni de la red, ni de dónde salen los
//  diccionarios: recibe un `estado` ({ idioma, tipo, capas }) y contesta. Lo usan utils/i18n.js (la
//  pantalla) y, con una copia de este mismo fichero en CommonJS, backend/services/i18nMotor.js (los
//  PDF y los WhatsApp). Las dos copias las compara backend/services/i18n-motor-gemelos.test.js con
//  los mismos casos: si tocas uno, toca el otro.
//
//  EL ORDEN, PARA UNA CLAVE (y su tipo de negocio, si lo hay: fisioterapia, podologia...):
//    1. cada capa del idioma, por orden: «clave@tipo» y después «clave»;
//    2. la capa del castellano (shared/i18n/es/vocabulario.json): el vocabulario y las variantes por
//       tipo de negocio, que también valen cuando el idioma es el castellano;
//    3. el texto por defecto, que es el castellano escrito en el código: t('clave', 'Texto').
//  El idioma gana a la variante: en inglés, «patient» sirve igual para un fisioterapeuta.
//
//  UNA TRADUCCIÓN DESFASADA NO SE ENSEÑA. Cada diccionario guarda, para cada clave, la huella del
//  castellano que se tradujo (`fuentes`). Si alguien cambia el texto en el código y nadie lo ha vuelto
//  a traducir, la huella ya no casa y sale el castellano nuevo: mejor en castellano que diciendo otra
//  cosa. La herramienta (herramientas/i18n/traducir.js) las lista como pendientes.
//
//  PLURALES: con `count`, Intl.PluralRules dice qué forma toca en ESE idioma (one, few, many, other) y
//  se busca «clave_one», «clave_few»... El texto por defecto puede ser { one: '1 cita', other: '{{count}} citas' }.
//  ANIDAR: «$t(voc.paciente)» dentro de un texto se sustituye por esa clave (hasta 4 niveles).
//  HUECOS: «{{nombre}}» se rellena con valores.nombre. Un hueco que no viene en los valores se deja
//  tal cual; uno que viene (aunque sea undefined) se escribe como lo escribiría una plantilla de JS.

export const RESERVA = 'es'

// FNV-1a de 32 bits, en hexadecimal: la huella del castellano. La misma en el servidor y en la herramienta.
export function huella(valor) {
  const s = typeof valor === 'string' ? valor : JSON.stringify(valor === undefined ? null : valor)
  let h = 0x811c9dc5
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i)
    h = Math.imul(h, 0x01000193) >>> 0
  }
  return ('0000000' + h.toString(16)).slice(-8)
}

export function formaPlural(idioma, n) {
  try { return new Intl.PluralRules(idioma || RESERVA).select(Number(n)) } catch (_) { return 'other' }
}

const esTexto = (x) => typeof x === 'string'

function buscarEnCapa(capa, clave, tipo, sufijos) {
  const tx = capa && capa.textos
  if (!tx || typeof tx !== 'object') return undefined
  for (const s of sufijos) {
    if (tipo) {
      const kv = clave + s + '@' + tipo
      if (esTexto(tx[kv])) return tx[kv]
    }
    const k = clave + s
    if (esTexto(tx[k])) return tx[k]
  }
  return undefined
}

// El texto SIN rellenar los huecos (después de elegir capa, plural y anidar).
export function plantilla(estado, clave, porDefecto, valores, prof = 0) {
  const e = estado || {}
  const v = valores || {}
  const conCuenta = v.count !== undefined && v.count !== null
  let texto
  for (const capa of Array.isArray(e.capas) ? e.capas : []) {
    if (!capa) continue
    const idiomaCapa = capa.idioma || e.idioma || RESERVA
    const sufijos = conCuenta ? ['_' + formaPlural(idiomaCapa, v.count), '_other', ''] : ['']
    const hallado = buscarEnCapa(capa, clave, e.tipo || null, sufijos)
    if (hallado === undefined) continue
    const f = capa.fuentes && capa.fuentes[clave]
    if (porDefecto !== undefined && porDefecto !== null && f && f !== huella(porDefecto)) continue   // desfasada
    texto = hallado
    break
  }
  if (texto === undefined) {
    if (porDefecto && typeof porDefecto === 'object') {
      // El texto por defecto es castellano: su plural lo decide la regla del castellano; o, si viene, `_forma` (la misma
      // comparación que tenía el código antes de pasar por el diccionario: «n === 1 ? '' : 's'»), para que salga igual.
      const forma = (typeof v._forma === 'string' && porDefecto[v._forma] !== undefined) ? v._forma : formaPlural(RESERVA, v.count)
      texto = porDefecto[forma] !== undefined ? porDefecto[forma] : porDefecto.other
    } else {
      texto = porDefecto
    }
  }
  if (texto === undefined || texto === null) return String(clave)
  texto = String(texto)
  if (prof < 4 && texto.indexOf('$t(') !== -1) {
    texto = texto.replace(/\$t\(([\w.@-]+)\)/g, (todo, k) => {
      const r = plantilla(e, k, undefined, undefined, prof + 1)
      return r === k ? todo : r
    })
  }
  return texto
}

const RE_HUECO = /\{\{\s*(\w+)\s*\}\}/g
const tiene = (o, k) => Object.prototype.hasOwnProperty.call(o, k)

// t(): SIEMPRE un texto. Los huecos se escriben como en una plantilla de JS (`${x}`).
export function traducir(estado, clave, porDefecto, valores) {
  const texto = plantilla(estado, clave, porDefecto, valores)
  const v = valores || {}
  if (texto.indexOf('{{') === -1) return texto
  return texto.replace(RE_HUECO, (todo, n) => (tiene(v, n) ? String(v[n]) : todo))
}

// tx(): para los hijos de JSX. Con huecos, una LISTA de trozos (texto, valor, texto...), que React pinta exactamente como
// pintaba los hijos sueltos de antes: un elemento como elemento, un false de `cond && <b/>` como nada, un texto como su
// propio nodo de texto (también en el HTML de renderToStaticMarkup, con sus <!-- --> entre medias). Sin huecos, un texto.
export function traducirEnTrozos(estado, clave, porDefecto, valores) {
  const texto = plantilla(estado, clave, porDefecto, valores)
  const v = valores || {}
  if (texto.indexOf('{{') === -1) return texto
  const trozos = []
  let desde = 0
  let m
  RE_HUECO.lastIndex = 0
  while ((m = RE_HUECO.exec(texto)) !== null) {
    if (m.index > desde) trozos.push(texto.slice(desde, m.index))
    trozos.push(tiene(v, m[1]) ? v[m[1]] : m[0])
    desde = m.index + m[0].length
  }
  if (desde < texto.length) trozos.push(texto.slice(desde))
  return trozos
}

// Une los diccionarios de una zona en una capa: { idioma, textos, fuentes }.
export function capaDe(idioma, diccionarios) {
  const textos = {}
  const fuentes = {}
  for (const d of Array.isArray(diccionarios) ? diccionarios : [diccionarios]) {
    if (!d || typeof d !== 'object') continue
    if (d.textos && typeof d.textos === 'object') Object.assign(textos, d.textos)
    if (d.fuentes && typeof d.fuentes === 'object') Object.assign(fuentes, d.fuentes)
  }
  return { idioma, textos, fuentes }
}

// Normaliza lo que alguien escribió a mano («Inglés», «english», «EN», «Русский») al código del catálogo, o null.
export function normalizarIdioma(texto, catalogo) {
  const s = String(texto == null ? '' : texto).trim().toLowerCase()
  if (!s) return null
  const sin = s.normalize('NFD').replace(/[̀-ͯ]/g, '').replace(/[()[\].,;:]/g, ' ').replace(/\s+/g, ' ').trim()
  const lista = (catalogo && Array.isArray(catalogo.idiomas)) ? catalogo.idiomas : []
  for (const i of lista) {
    const alias = [i.codigo, String(i.nombre || '').toLowerCase(), String(i.nombre_es || '').toLowerCase()].concat(i.alias || [])
    const limpios = alias.map((a) => String(a).toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, ''))
    if (limpios.includes(sin)) return i.codigo
  }
  // «inglés (británico)», «es-ES», «en_GB»: se mira la primera palabra o el código antes de la región.
  const primero = sin.split(/[\s_/-]/)[0]
  if (primero && primero !== sin) return normalizarIdioma(primero, catalogo)
  return null
}
